import * as React from "react";
import { DashboardFilters, DataCube, DimensionTypes } from "../DashboardDataSource/DataCube";
import * as cn from "classnames";
import styles = require("./style.less");
import DashboardCell from "./DashboardCell/DashboardCell";
import { TaskState } from "client/resources";
import PaperLayout from "components/PaperLayout/index";
import { NavLink } from "react-router-dom";
import { NavigationButton } from "../../../../components/Button/NavigationButton";
import { isEqual, flatten } from "lodash";
import routeLinks from "../../../../routeLinks";
import { DeploymentCreateGoal } from "../Releases/ReleasesRoutes/releaseRouteLinks";
import { DataSet, getDataSet } from "../DashboardDataSource/DataSet";

interface ProjectDashboardProps {
    cube: DataCube;
    filters: DashboardFilters;
    maximumRows?: number;
    allowDeployments?: boolean;
    showDeploymentCounts?: boolean;
    flatStyle?: boolean;
    onProjectCountChanged?(projectCount: number): void;
}

type Group = { groupId: string, rowsInGroup: string[] };

type DeploymentContext = { environment: string, release: string, tenant: string, project: string };

export default class ProjectDashboard extends React.Component<ProjectDashboardProps> {
    constructor(props: any) {
        super(props);
    }

    shouldComponentUpdate(nextProps: ProjectDashboardProps) {
        return !isEqual(getPropsForComparison(nextProps), getPropsForComparison(this.props));

        function getPropsForComparison(props: ProjectDashboardProps) {
            const {
                    onProjectCountChanged,
                    ...rest
                } = props;
            return rest;
        }
    }

    render() {
        const dataSet = getDataSet(this.props.filters, this.props.cube);
        const deploymentContext = this.getDeploymentContext(dataSet);
        const groups = this.getProjectRowsLimitedToMaximum(dataSet, this.props.maximumRows);

        if (this.props.onProjectCountChanged) {
            // We calling this in a timeout because the parent sets state
            setTimeout(() => this.props.onProjectCountChanged(flatten(groups.map(g => g.rowsInGroup)).length), 0);
        }

        return <div>
            {this.redrawMatrix(dataSet, groups, deploymentContext)}
        </div>;
    }

    private getDeploymentContext(dataSet: DataSet): (groupId: string, rowId: string, columnId: string) => DeploymentContext {
        return (groupId: string, rowId: string, columnId: string) => ({
            environment: this.tryGetValue(dataSet, DimensionTypes.Environment)(groupId, rowId, columnId),
            release: this.tryGetValue(dataSet, DimensionTypes.Release)(groupId, rowId, columnId),
            tenant: this.tryGetValue(dataSet, DimensionTypes.Tenant)(groupId, rowId, columnId),
            project: this.tryGetValue(dataSet, DimensionTypes.Project)(groupId, rowId, columnId)
        });
    }

    private tryGetValue(dataSet: DataSet, dimensionType: DimensionTypes): (groupId: string, rowId: string, columnId: string) => string {
        const filters = this.props.filters;
        if (dataSet.groupDimension === dimensionType) {
            return (groupId, rowId, columnId) => groupId;
        } else if (dataSet.rowDimension === dimensionType) {
            return (groupId, rowId, columnId) => rowId;
        } else if (dataSet.columnDimension === dimensionType) {
            return (groupId, rowId, columnId) => columnId;
        } else if (filters[dimensionType]) {
            const environmentIds = Object.keys(filters[dimensionType]);
            if (environmentIds.length === 1) {
                return (groupId, rowId, columnId) => environmentIds[0];
            }
        }
        return () => null;
    }

    private buildColumnTitle(cube: DataCube, dataSet: DataSet, groupId: string, columnId: string) {
        const environmentId = columnId;
        if (dataSet.columnDimension === DimensionTypes.Environment &&
            dataSet.rowDimension === DimensionTypes.Tenant &&
            this.props.filters[DimensionTypes.Release]) {
            const releaseFilters = Object.keys(this.props.filters[DimensionTypes.Release]);
            if (releaseFilters.length === 1) {

                const releaseId = Object.keys(this.props.filters[DimensionTypes.Release])[0];
                const projectId = Object.keys(this.props.filters[DimensionTypes.Project])[0];
                const groupTenantsWithoutSuccess = dataSet.getRowsForGroup(groupId)
                    .filter(tenantId =>
                        (tenantId !== null && cube.tenantIndex[tenantId].ProjectEnvironments[projectId].indexOf(environmentId) !== -1) &&
                        (!dataSet.matrix[groupId] ||
                            !dataSet.matrix[groupId][tenantId] ||
                            !dataSet.matrix[groupId][tenantId][environmentId] ||
                            !dataSet.matrix[groupId][tenantId][environmentId].find(item => item.State === TaskState.Success)));
                const availableDeployments = ((cube.nextAvailableDeployments[releaseFilters[0]] || {})[environmentId]);
                if (availableDeployments) {
                    const canDeployAll = availableDeployments.filter(t => groupTenantsWithoutSuccess.indexOf(t) !== -1);
                    if (canDeployAll.length > 0) {
                        const uri = routeLinks.project(cube.projectIndex[projectId])
                            .release(cube.releaseIndex[releaseId] ? cube.releaseIndex[releaseId].Version : releaseId)
                            .deployments.create(DeploymentCreateGoal.To, environmentId, groupTenantsWithoutSuccess);

                        return <div className={styles.deployAllCell}>
                            {dataSet.columnTitle(columnId)}
                            <NavigationButton label="Deploy All" href={uri} />
                        </div>;
                    }
                }
            }
        }
        return dataSet.columnTitle(columnId);
    }

    private emptyGroupMessage = (dataSet: DataSet, groupId: string): React.ReactNode => {
        const projectId = this.tryGetValue(dataSet, DimensionTypes.Project)(null, null, null);
        if (projectId === null) {
            return null;
        }

        const releaseCreateUri = routeLinks.project(this.props.cube.projectIndex[projectId]).releaseCreate;

        if (dataSet.groupDimension === DimensionTypes.Channel) {
            return <div className={styles.emptyCell}>
                There are no releases for this channel yet. <NavLink
                    to={`${releaseCreateUri}?channelId=${groupId}`}>Create a release</NavLink>
            </div>;
        } else if (dataSet.groupDimension === DimensionTypes.None) {
            return <div className={styles.emptyCell}>
                There are no releases for this project yet. <NavLink
                    to={releaseCreateUri}>Create a release</NavLink>
            </div>;
        }
        return null;
    }

    private redrawMatrix(dataSet: DataSet, groups: Group[], deploymentContext: (groupId: string, rowId: string, columnId: string) => DeploymentContext) {
        if (!dataSet) {
            return null;
        }

        return groups.map(g => {
            const groupId = g.groupId;
            const rowsInGroup = g.rowsInGroup;
            const emptyGroupMessage = this.emptyGroupMessage(dataSet, groupId);

            if (rowsInGroup.length === 0 && !emptyGroupMessage) {
                return null;
            }

            const columnsInGroup = dataSet.getColumnsForGroup(groupId);
            const headers = columnsInGroup.map(columnId =>
                <th key={columnId} className={styles.columnHeads}>
                    <div className={styles.centerCell}>
                        {this.buildColumnTitle(this.props.cube, dataSet, groupId, columnId)}
                    </div>
                </th>);

            const group = dataSet.matrix[groupId] || {};
            const rows = rowsInGroup.length === 0 ?
                <tr>
                    <td colSpan={columnsInGroup.length + 2}>
                        {emptyGroupMessage}
                    </td>
                </tr> :
                rowsInGroup.map(rowId => {
                    const row = group[rowId] || {};
                    const columns = columnsInGroup.map(columnId =>
                        <td key={columnId + rowId} className={styles.cellz}>
                            <DashboardCell deployments={row[columnId]}
                                deploymentContext={deploymentContext(groupId, rowId, columnId)}
                                allowDeployments={this.props.allowDeployments}
                                showDeploymentCounts={this.props.showDeploymentCounts}
                                cube={this.props.cube} />
                        </td>);

                    return <tr key={rowId}>
                        <td className={cn(styles.headerCell)}>
                            {dataSet.rowTitle(rowId)}
                        </td>
                        {columns}
                        <td style={{ width: "auto" }} />
                    </tr>;
                });

            return <PaperLayout key={groupId}
                fullWidth={true}
                innerClassName={styles.container}
                flatStyle={this.props.flatStyle}>
                <table cellPadding={0} cellSpacing={0} className={styles.dashboardTable}>
                    <thead>
                        <tr>
                            <th className={styles.columnHeads}>
                                <div className={styles.centerCell}>
                                    {dataSet.groupDimension === DimensionTypes.None || groups.length === 1 ?
                                        dataSet.rowLabel() :
                                        dataSet.groupTitle(groupId)}
                                </div>
                            </th>
                            {headers}
                            <th style={{ width: "auto" }} />
                        </tr>
                    </thead>
                    <tbody>
                        {rows}
                    </tbody>
                </table>
            </PaperLayout>;
        });
    }

    private getProjectRowsLimitedToMaximum(dataSet: DataSet, maximumRows: number): Group[] {
        const hasLimit = maximumRows !== null && maximumRows !== undefined && maximumRows > 0;
        let maxRows = hasLimit ? maximumRows : null;

        // get rows for each group, track our use against the limit
        const groups: Group[] = dataSet.getGroups().reduce((acc, groupId) => {
            const value = {
                groupId,
                rowsInGroup: dataSet.getRowsForGroup(groupId, maxRows)
            };
            if (hasLimit) {
                maxRows = maxRows - value.rowsInGroup.length;
            }
            acc.push(value);
            return acc;
        }, []);

        return groups;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/areas/projects/components/ProjectDashboard/ProjectDashboard.tsx